import React from 'react';
import { ExternalLink } from 'lucide-react';

const PrivacyPolicy = () => {
  const privacyUrl = 'https://vbwaup6y.kartra.com/page/4D32084';

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-lg shadow-lg p-8">
          <div className="mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Privacy Policy</h1>
            <p className="text-gray-600 text-lg">
              Your privacy is important to us. Learn how we collect, use, and protect your personal information.
            </p>
          </div>

          <div className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded-lg mb-8">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-blue-900 mb-2">View Full Privacy Policy</h3>
                <p className="text-blue-800 mb-4">
                  Our complete Privacy Policy is hosted on our secure legal page. 
                  Click the button below to view the full document with all details about how we handle your data.
                </p>
                <a
                  href={privacyUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors shadow-md hover:shadow-lg"
                >
                  <span>View Full Privacy Policy</span>
                  <ExternalLink className="w-5 h-5" />
                </a>
              </div>
            </div>
          </div>

          <div className="prose prose-lg max-w-none">
            <div className="mb-6 p-4 bg-indigo-50 border-l-4 border-indigo-500 rounded-lg">
              <p className="text-sm text-gray-700">
                <strong>Data Controller:</strong> Pete Murray Ltd (trading as ElderFit Secrets)<br />
                <strong>Company:</strong> Pete Murray Ltd<br />
                <strong>Trading Name:</strong> ElderFit Secrets
              </p>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-4">Privacy Overview</h2>
            <p className="text-gray-700 mb-6">
              At ElderFit Secrets (operated by Pete Murray Ltd), we are committed to protecting your privacy and personal information. 
              Here's a summary of how we handle your data:
            </p>

            <div className="space-y-6">
              <div className="border-l-4 border-indigo-500 pl-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Information We Collect</h3>
                <p className="text-gray-700 mb-2">
                  We collect information that you provide directly to us, including:
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
                  <li>Account information (name, email, password)</li>
                  <li>Health and fitness data you choose to share</li>
                  <li>Payment information (processed securely through third-party processors)</li>
                  <li>Usage data and preferences</li>
                </ul>
              </div>

              <div className="border-l-4 border-indigo-500 pl-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">How We Use Your Information</h3>
                <p className="text-gray-700 mb-2">
                  We use your information to:
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
                  <li>Provide and improve our fitness services</li>
                  <li>Process payments and manage subscriptions</li>
                  <li>Send you important updates about your account</li>
                  <li>Personalize your workout experience</li>
                  <li>Ensure the security of our platform</li>
                </ul>
              </div>

              <div className="border-l-4 border-indigo-500 pl-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Data Protection</h3>
                <p className="text-gray-700">
                  We implement industry-standard security measures to protect your personal information. 
                  We use secure payment processors and do not store full credit card information on our servers. 
                  Your data may be stored on servers located outside your country of residence, but we ensure 
                  appropriate safeguards are in place.
                </p>
              </div>

              <div className="border-l-4 border-indigo-500 pl-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Your Rights (GDPR/UK)</h3>
                <p className="text-gray-700 mb-2">
                  If you're located in the European Union or United Kingdom, you have the right to:
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
                  <li>Access your personal data</li>
                  <li>Rectify or correct your data</li>
                  <li>Request deletion of your data</li>
                  <li>Object to processing of your data</li>
                  <li>Data portability</li>
                </ul>
              </div>

              <div className="border-l-4 border-indigo-500 pl-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Third-Party Services</h3>
                <p className="text-gray-700">
                  We may use third-party services for payment processing, analytics, and other functions. 
                  These services have their own privacy policies, and we encourage you to review them. 
                  We only share information necessary for these services to function.
                </p>
              </div>

              <div className="border-l-4 border-indigo-500 pl-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Cookies and Tracking</h3>
                <p className="text-gray-700">
                  We use cookies and similar technologies to enhance your experience, analyze usage, 
                  and improve our services. You can control cookie preferences through your browser settings.
                </p>
              </div>

              <div className="border-l-4 border-indigo-500 pl-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Data Retention</h3>
                <p className="text-gray-700">
                  We retain your personal information for as long as necessary to provide our services 
                  and fulfill the purposes outlined in this policy, or as required by law. When you 
                  cancel your account, we will delete or anonymize your data in accordance with our 
                  retention policies.
                </p>
              </div>
            </div>

            <div className="mt-8 p-6 bg-gray-50 rounded-lg border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Questions About Privacy?</h3>
              <p className="text-gray-700 mb-4">
                If you have questions about our Privacy Policy or how we handle your data, please contact us:
              </p>
              <p className="text-gray-700">
                <strong>Email:</strong>{' '}
                <a 
                  href="mailto:support@elderfitsecrets.com" 
                  className="text-indigo-600 hover:text-indigo-700 underline"
                >
                  support@elderfitsecrets.com
                </a>
              </p>
            </div>

            <div className="mt-8 flex justify-center">
              <a
                href={privacyUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-8 py-4 bg-indigo-600 text-white font-semibold rounded-lg hover:bg-indigo-700 transition-colors shadow-md hover:shadow-lg text-lg"
              >
                <span>Read Full Privacy Policy</span>
                <ExternalLink className="w-5 h-5" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;

